import React from "react";
import { motion, useInView } from "framer-motion";

const defaultTransition = {
  duration: 0.3,
  ease: [0.25, 0.1, 0.25, 1], // ease-out
};

const defaultVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

/**
 * Subtle scroll reveal: fade + translateY 20px → 0.
 * 200–400ms, ease-out. Premium SaaS—never slows scrolling.
 */
export function ScrollReveal({
  children,
  className = "",
  transition = defaultTransition,
  amount = 0.1,
  once = true,
}) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { amount, once });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={defaultVariants}
      transition={transition}
    >
      {children}
    </motion.div>
  );
}

/**
 * Container for staggered children. Stagger 0.1–0.15s.
 */
const staggerContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      staggerDirection: 1,
    },
  },
};

export function ScrollRevealStagger({
  children,
  className = "",
  amount = 0.1,
  once = true,
}) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { amount, once });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainerVariants}
    >
      {children}
    </motion.div>
  );
}

const staggerItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

/**
 * Use with ScrollRevealStagger: wrap each child in this so they stagger.
 */
export function ScrollRevealStaggerItem({ children, className = "", transition = defaultTransition }) {
  return (
    <motion.div
      className={className}
      variants={staggerItemVariants}
      transition={transition}
    >
      {children}
    </motion.div>
  );
}

export default ScrollReveal;
